import { useState, useEffect, useCallback } from "react";
import { driver, DriveStep } from "driver.js";
import "driver.js/dist/driver.css";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/components/auth/AuthProvider";

export const useOnboarding = () => {
  const [hasCompletedOnboarding, setHasCompletedOnboarding] = useState<boolean | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { user } = useAuth();

  // Check if user has completed onboarding
  useEffect(() => {
    const checkOnboardingStatus = async () => {
      if (!user) {
        setIsLoading(false);
        return;
      }

      try {
        const { data, error } = await supabase
          .from("profiles")
          .select("onboarding_completed")
          .eq("id", user.id)
          .single();

        if (error) {
          console.error("Error checking onboarding status:", error);
          setHasCompletedOnboarding(false);
        } else {
          setHasCompletedOnboarding(data?.onboarding_completed ?? false);
        }
      } catch (error) {
        console.error("Error checking onboarding status:", error);
        setHasCompletedOnboarding(false);
      } finally {
        setIsLoading(false);
      }
    };

    checkOnboardingStatus();
  }, [user]);

  // Mark onboarding as completed
  const completeOnboarding = useCallback(async () => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from("profiles")
        .update({ onboarding_completed: true })
        .eq("id", user.id);

      if (error) {
        console.error("Error updating onboarding status:", error);
      } else {
        setHasCompletedOnboarding(true);
      }
    } catch (error) {
      console.error("Error updating onboarding status:", error);
    }
  }, [user]);

  // Start the tour
  const startTour = useCallback(() => {
    const steps: DriveStep[] = [
      {
        element: '[data-tour="progress-bar"]',
        popover: {
          title: "📊 Track Your Progress",
          description: "This bar shows your overall progress across all habits. Watch it grow as you complete your daily goals!",
          side: "bottom",
          align: "center",
        },
      },
      {
        element: '[data-tour="add-habit"]',
        popover: {
          title: "➕ Create Your First Habit",
          description: "Click here to add a new habit. Set a goal, choose an emoji, and define your daily targets!",
          side: "bottom",
          align: "start",
        },
      },
      {
        element: '[data-tour="filter-buttons"]',
        popover: {
          title: "🔍 Filter Your View",
          description: "Toggle between active and completed habits to stay organized and celebrate your wins!",
          side: "bottom",
          align: "start",
        },
      },
      {
        element: '[data-tour="category-sidebar"]',
        popover: {
          title: "📁 Organize by Categories",
          description: "Create custom categories to group your habits. Stay organized and focused on what matters most!",
          side: "right",
          align: "start",
        },
      },
      {
        popover: {
          title: "🎉 You're All Set!",
          description: "Start building positive habits today. Remember: small daily actions lead to big results!",
        },
      },
    ];

    const driverObj = driver({
      showProgress: true,
      steps,
      nextBtnText: "Next →",
      prevBtnText: "← Back",
      doneBtnText: "Get Started!",
      progressText: "{{current}} of {{total}}",
      popoverClass: "onboarding-popover",
      onDestroyStarted: () => {
        completeOnboarding();
        driverObj.destroy();
      },
    });

    driverObj.drive();
  }, [completeOnboarding]);

  return {
    hasCompletedOnboarding,
    isLoading,
    startTour,
    completeOnboarding,
  };
};
